import { SideNav, SideNavContent } from "components";
import PageHeader from "components/UniversalComponents/PageHeader";
import { HeaderDasNew, MainBody, OverlayLoader } from "components/core";
import { WidthLimiter } from "pages/PlanSetup/styles";
import React, { useEffect, useMemo, useState } from "react";
import EditIcon from "assets/svgs/edit.svg";
import http from "services/httpService";
import styled from "styled-components";
import ServicingManager from "pages/ClaimSupport/ServicingManager";
import TextInline from "pages/ClaimSupport/TextInline";
import Level from "pages/ClaimSupport/level";
import useFetchSupport from "./useFetchSupport";
import BaseBreadcrumbs from "components/core/BaseBreadcrumbs";
import routes from "components/routes";
import { useNavigate, useSearchParams } from "react-router-dom";
import { FLEX_PRODUCTS } from "constants";
import { clearPlanSetupCompletion } from "utlis/setupUtils";
import { Ptag } from "components/core/fonts";
import { Box } from "@mui/material";
import { roleIsAdmin } from "utlis/utils";

function ClaimSupport() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const userplanId = params.get("userplan");
  const { support, tpaName, loading } = useFetchSupport(userplanId);

  const cashlessClaims = useMemo(() => {
    if (
      !support ||
      !support.claims_details ||
      !support.claims_details.cashless_details
    )
      return [];
    const detail = Object.groupBy(
      support.claims_details.cashless_details,
      ({ cashless_level }) => cashless_level
    );
    return Object.values(detail);
  }, [support]);

  const escalationDetails = useMemo(() => {
    if (
      !support ||
      !support.claims_details ||
      !support.claims_details.escalation_details
    )
      return [];

    const detail = Object.groupBy(
      support.claims_details.escalation_details,
      ({ escalation_level }) => escalation_level
    );
    return Object.values(detail);
  }, [support]);

  const neoManagers = useMemo(() => {
    if (!support) return [];
    if (
      support.relationship_manager.length > 0 ||
      support.servicing_manager.length > 0
    ) {
      return [...support.servicing_manager, ...support.relationship_manager];
    }
  }, [support]);

  const RenderContacts = (header, cashlessClaim, data) => {
    return (
      <>
        <div style={{ marginTop: "5rem" }}>
          <PageHeader header={header} />
        </div>
        <Level data={data} cashlessClaim={cashlessClaim} />
      </>
    );
  };

  const crumbs = useMemo(() => {
    return [
      {
        name: "Dashboard",
        path: "/v1",
      },
      {
        name: FLEX_PRODUCTS[params.get("product")] || "",
        // path: `/v1?tab=${}`,
      },
      {
        name: "Claim Support",
        path: "/v1/claim-support",
        active: true,
      },
    ];
  }, []);

  const handleEditAdministrator = () => {
    if (userplanId) {
      localStorage.setItem("eb_current_plan", userplanId);
    }
    clearPlanSetupCompletion();
    navigate(`/${routes.UNIVERSAL_TPA}`, {
      state: {
        tpaPrefill: support,
        tpaUserplanUniqueCode: support?.claims_details?.partner_id || "",
        productLabel: FLEX_PRODUCTS[params.get("product")] || "",
        returnTo: `/v1/claim-support?userplan=${userplanId}&product=${
          params.get("product") || ""
        }`,
      },
    });
  };

  if (loading) return <OverlayLoader />;

  return (
    <>
      <BaseBreadcrumbs breadcrumbs={crumbs} />
      {tpaName && (
        <WidthLimiter
          style={{
            overflow: "hidden",
            maxWidth: "1000px",
          }}
        >
          <HeaderWithEdit>
            <PageHeader
              header="Administrator"
              styles={{ marginBottom: 0 }}
            />
            {roleIsAdmin() && <EditButton
              type="button"
              aria-label="Edit administrator"
              onClick={handleEditAdministrator}
            >
              <img src={EditIcon} alt="" />
            </EditButton>}
          </HeaderWithEdit>
          {tpaName && (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: { xs: "14px", sm: "20px" },
                padding: { xs: "16px", sm: "20px" },
                backgroundColor: "#fdefcc",
                borderRadius: "10px",
              }}
            >
              <Box sx={{ minWidth: 0 }}>
                <Ptag style={{ fontSize: "12px", fontWeight: "400" }}>
                  {"TPA Name"}
                </Ptag>
                <Ptag
                  style={{
                    fontSize: "18px",
                    fontWeight: "600",
                    marginTop: "8px",
                    lineHeight: "28px",
                    wordBreak: "break-word",
                  }}
                >
                  {tpaName}
                </Ptag>
              </Box>

              <Box sx={{ minWidth: 0 }}>
                <Ptag style={{ fontSize: "12px", fontWeight: "400" }}>
                  {"Partner Corporate ID"}
                </Ptag>
                <Ptag
                  style={{
                    fontSize: "18px",
                    fontWeight: "600",
                    marginTop: "8px",
                    lineHeight: "28px",
                    wordBreak: "break-word",
                  }}
                >
                  {support?.claims_details?.partner_id || "N/A"}
                </Ptag>
              </Box>
            </Box>
          )}
          {cashlessClaims.length > 0 &&
            RenderContacts("Contact Details", "cashless", cashlessClaims)}
          {escalationDetails.length > 0 &&
            RenderContacts(
              "Escalation Matrix",
              "escalation",
              escalationDetails
            )}
        </WidthLimiter>
      )}
      {tpaName && <PageGap />}
      <WidthLimiter
        style={{
          maxWidth: "1000px",
        }}
      >
        <PageHeader
          header={"Contacts for Neo Risk Management and Insurance Broker"}
        />
        <ServicingManager data={neoManagers} />
      </WidthLimiter>
    </>
  );
}

export default ClaimSupport;

const PageGap = styled.div`
  height: 1px;
  margin: 86px 0px 52px 0px;
  position: relative;
  background-color: #dadada;
`;

const HeaderWithEdit = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 24px;
`;

const EditButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  padding: 0;
  border: 0;
  background: transparent;
  cursor: pointer;

  img {
    width: 18px;
    height: 18px;
  }
`;
